import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { changePassword, login, respondToPasswordChallenge  } from "../../services/loginApi.jsx";
import { toast } from "react-toastify";
import { ContextApp } from "../../utils/Context";
import { ConfigContext } from "../../utils/Config.jsx";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { Button } from "../ui/button.tsx";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card.tsx";
import { Input } from "../ui/input.tsx";
import { Info, AlertTriangle, CheckCircle2, Loader2, CheckCheck, X, AlertCircle } from "lucide-react";
import { useTheme } from "../ThemeContext";
import { CustomAlert } from "../ui/CustomAlert.tsx";
import { ADLoginButton } from "../ui/ADLoginButton.jsx";
import Cookies from "js-cookie";
import { ApiContext } from "../../utils/ApiContext.jsx";
import fetchWithAuth from "../../utils/fetchWithAuth.jsx";

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDarkMode } = useTheme();
  const { setIsLoggedIn, setIsGuest, setUsername } = useContext(ContextApp);
  const apiContext = useContext(ApiContext);
  const configContext = useContext(ConfigContext);
  const { config, isLoading: isConfigLoading } = configContext || { config: { GUEST_ALLOWED: true }, isLoading: false };
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [needPasswordChange, setNeedPasswordChange] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [session, setSession] = useState("");
  const [textConfig, setTextConfig] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isADLoading, setIsADLoading] = useState(false);
  const [loginStatus, setLoginStatus] = useState(null);
  const [showADButton, setShowADButton] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    open: false,
    type: "success",
    message: "",
    title: "",
    onConfirm: undefined,
  });

  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  const generateRandomString = (length) => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return result;
  };

  const sha256 = async (plain) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return hash;
  };

  const base64urlencode = (arrayBuffer) => {
    const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  const handleADLogin = async () => {
    try {
      setIsADLoading(true);
      
      const configResponse = await fetch('/configuration.json');
      const config = await configResponse.json();
      
      const { COGNITO_DOMAIN, COGNITO_APP_CLIENT_ID, IDENTITY_PROVIDER_NAME } = config;
      
      const codeVerifier = generateRandomString(128);
      const codeChallenge = base64urlencode(await sha256(codeVerifier));
      const state = generateRandomString(32);
      
      sessionStorage.setItem('pkce_verifier', codeVerifier);
      sessionStorage.setItem('pkce_state', state);
      
      const authParams = new URLSearchParams({
        response_type: 'code',
        client_id: COGNITO_APP_CLIENT_ID,
        redirect_uri: `${window.location.origin}/callback-ad`,
        scope: 'email openid profile',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        state: state,
        identity_provider: IDENTITY_PROVIDER_NAME
      });
      
      const authUrl = `https://${COGNITO_DOMAIN}/authorize?${authParams.toString()}`;
      
      window.location.href = authUrl;
      
    } catch (error) {
      console.error('Error iniciando login AD:', error);
      toast.error(textConfig.LOGIN_AD_ERROR || 'Error al iniciar sesión con Active Directory');
      setIsADLoading(false);
    }
  };

  const validatePassword = (password) => {
    setPasswordValidations({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>\-_\\\/\[\]+=]/.test(password),
    });
  };

  const isPasswordValid = () => {
    return Object.values(passwordValidations).every((validation) => validation);
  };
  
  const handleSuccessfulLogin = async (username) => {
    try {
      const API_ASSOCIATION = apiContext.endpoints.API_ASSOCIATION;

      if (!username) {
        console.error(textConfig.LOGIN_ERROR_NO_USERNAME || "No se encontró nombre de usuario después del login");
        setLoginStatus('error');
        return;
      }
      
      const associationUrl = `${API_ASSOCIATION}?user_id=${username}`;
      
      const token = Cookies.get("accessToken");
      const associationResponse = await fetchWithAuth(associationUrl, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (!associationResponse.ok) {
        setAlertConfig({
          open: true,
          type: "error",
          message: textConfig.LOGIN_NO_COLLECTION_ACCESS || "Tu cuenta no tiene acceso a ninguna colección. Contacta con el administrador para obtener acceso.",
          title: textConfig.LOGIN_LIMITED_ACCESS_TITLE || "Acceso limitado",
          onConfirm: undefined,
        });
        return false;
      } 
      
      const associationData = await associationResponse.json();
      if (!associationData || !associationData.profile_id) {
        setAlertConfig({
          open: true,
          type: "error",
          message: textConfig.LOGIN_NO_PROFILE_ACCESS || "Tu cuenta no tiene acceso al perfil requerido. Contacta con el administrador.",
          title: textConfig.LOGIN_LIMITED_ACCESS_TITLE || "Acceso limitado",
          onConfirm: undefined,
        });
        return false;
      }
      
      return true;
    } catch (error) {
      console.error(textConfig.LOGIN_ERROR_ACCESS_VERIFICATION || "Error al verificar acceso después del login:", error);
      return true; 
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchTextConfig = async () => {
    try {
      const [textResponse, configResponse] = await Promise.all([
        fetch("/text.json"),
        fetch("/configuration.json")
      ]);
      
      const textData = await textResponse.json();
      setTextConfig(textData);
      
      if (configResponse.ok) {
        const configData = await configResponse.json();
        const hasADProvider = configData.IDENTITY_PROVIDER_NAME && 
                             configData.IDENTITY_PROVIDER_NAME.trim() !== '';
        setShowADButton(hasADProvider);
      } else {
        setShowADButton(false);
      }
    } catch (error) {
      console.error("Error loading configs:", error);
      setShowADButton(false);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  useEffect(() => {
    validatePassword(newPassword);
  }, [newPassword]);

  useEffect(() => {
    const error = searchParams.get('error');
    if (error) {
      let errorMessage = '';
      switch (error) {
        case 'invalid_state':
          errorMessage = textConfig.LOGIN_ERROR_INVALID_STATE || 'Error de seguridad detectado. Por favor, intenta iniciar sesión nuevamente.';
          break;
        case 'token_exchange_failed':
          errorMessage = textConfig.LOGIN_ERROR_TOKEN_EXCHANGE || 'Error del servidor. Por favor, intenta más tarde.';
          break;
        case 'callback_failed':
          errorMessage = textConfig.LOGIN_ERROR_CALLBACK || 'Ocurrió un error inesperado. Por favor, intenta nuevamente.';
          break;
        case 'config_load_failed':
          errorMessage = textConfig.LOGIN_ERROR_CONFIG_LOAD || 'Error al cargar la configuración del sistema. Por favor, intenta más tarde.';
          break;
        default:
          errorMessage = textConfig.LOGIN_ERROR_GENERAL || 'Error de autenticación. Por favor, intenta nuevamente.';
      }
      
      setErrorMessage(errorMessage);
      toast.error(errorMessage);
      
      navigate('/login', { replace: true });
    }
  }, [searchParams, textConfig, navigate]);

  const togglePasswordVisibility = (field) => {
    switch (field) {
      case 'password':
        setShowPassword(!showPassword);
        break;
      case 'newPassword':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirmPassword':
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };

  const handleGuestLogin = () => {
    if (!config?.GUEST_ALLOWED) return;
    setIsLoggedIn(false);
    setIsGuest(true);
    navigate("/chat");
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginStatus(null);
    
    if (!isPasswordValid()) {
      setErrorMessage(textConfig.LOGIN_PASSWORD_CRITERIA_ERROR);
      toast.error(textConfig.LOGIN_PASSWORD_CRITERIA_ERROR);
      setIsLoading(false);
      setLoginStatus('error');
      
      setTimeout(() => {
        setLoginStatus(null);
      }, 3000);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setErrorMessage(textConfig.LOGIN_PASSWORDS_MISMATCH_ERROR);
      toast.error(textConfig.LOGIN_PASSWORDS_MISMATCH_ERROR);
      setIsLoading(false);
      setLoginStatus('error');
      
      setTimeout(() => {
        setLoginStatus(null);
      }, 3000);
      return;
    }
    if (newPassword === password) {
      setErrorMessage(textConfig.LOGIN_PASSWORD_SAME_AS_OLD);
      toast.error(textConfig.LOGIN_PASSWORD_SAME_AS_OLD);
      setIsLoading(false);
      setLoginStatus('error');
      
      setTimeout(() => {
        setLoginStatus(null);
      }, 3000);
      return;
    }
    try {
      setAlertConfig({
        open: true,
        type: "loading",
        message: textConfig.LOGIN_CHANGING_PASSWORD,
        title: "",
        onConfirm: undefined,
      });
      
      const response = await respondToPasswordChallenge(user, password, newPassword, session);
      setLoginStatus('success');
      
      setAlertConfig({
        open: true,
        type: "success",
        message: textConfig.LOGIN_PASSWORD_CHANGE_SUCCESS_MESSAGE,
        title: textConfig.LOGIN_SUCCESS_TITLE,
        onConfirm: undefined,
      });
      
      setTimeout(() => {
        setUsername(user);
        localStorage.setItem('username', user);
        Cookies.set('username', user);
        
        setIsLoggedIn(true);
        setIsGuest(false);
        
        setNeedPasswordChange(false);
        setNewPassword("");
        setConfirmNewPassword("");
        setSession("");
        setErrorMessage("");
        setPasswordValidations({
          length: false,
          uppercase: false,
          lowercase: false,
          number: false,
          special: false,
        });
        
        setAlertConfig(prev => ({ ...prev, open: false }));
        setIsLoading(false);
        setLoginStatus(null);
        
        window.location.href = "/";
      }, 2000);
      
    } catch (error) {
      console.error(error);
      setErrorMessage(textConfig.LOGIN_PASSWORD_CHANGE_ERROR);
      
      setAlertConfig({
        open: true,
        type: "error",
        message: textConfig.LOGIN_PASSWORD_CHANGE_ERROR,
        title: textConfig.LOGIN_ERROR_TITLE,
        onConfirm: undefined,
      });
      setIsLoading(false);
      setLoginStatus('error');
      
      setTimeout(() => {
        setLoginStatus(null);
      }, 3000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginStatus(null);
    toast.dismiss();
    toast.info(textConfig.LOGIN_TRYING_LOGIN);
    try {
      const response = await login(user, password);
      toast.dismiss();
      if (response.ChallengeName === "NEW_PASSWORD_REQUIRED") {
        setNeedPasswordChange(true);
        setSession(response.Session);
        toast.success(textConfig.LOGIN_NEED_CHANGE_PASSWORD);
        setErrorMessage(textConfig.LOGIN_NEED_CHANGE_PASSWORD);
        setIsLoading(false);
        setLoginStatus('success');
      } else {
        toast.success(textConfig.LOGIN_TOAST_LOGIN_SUCCESS);
        setLoginStatus('success');
        
        setTimeout(async () => {
          const hasAccess = await handleSuccessfulLogin(user);
          setUsername(user);
          localStorage.setItem('username', user);
          Cookies.set('username', user);
          setIsLoggedIn(true);
          setIsGuest(false);
          
          window.location.href = "/";
        }, 800);
        
        return;
      }
    } catch (error) {
      console.error(textConfig.LOGIN_ERROR_DETAILED || "Error detallado:", error);
      toast.dismiss();
      setErrorMessage(textConfig.LOGIN_TOAST_LOGIN_FAIL);
      toast.error(textConfig.LOGIN_TOAST_LOGIN_FAIL, { autoClose: 5000 });
      setIsLoading(false);
      setLoginStatus('error');  
      setTimeout(() => {
        setLoginStatus(null);
      }, 3000);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row items-center justify-center p-4 lg:p-20">
      <div className={`w-full lg:w-1/2 space-y-4 lg:pr-8 mb-8 lg:mb-0 ${
        isDarkMode ? "text-white" : "text-black"
      }`}>
        <h1 className={`text-3xl lg:text-5xl font-bold ${
          isDarkMode ? "text-white" : "text-black"
        }`}>
          {textConfig.LOGIN_WELCOME_TITLE}
        </h1>
        <h2 className={`text-xl lg:text-3xl pt-8 text-green-500`}>
          {textConfig.LOGIN_SUBTITLE}
        </h2>
        <p className={`text-lg font-semibold ${
          isDarkMode ? "text-gray-400" : "text-gray-700"
        }`}>
          {textConfig.LOGIN_DESCRIPTION}  
        </p>
      </div>

      <div className="w-full lg:w-1/2 max-w-md">
        <Card>
          <CardHeader className="relative">
           <CardTitle>{needPasswordChange ? textConfig.LOGIN_PASSWORD_CHANGE : textConfig.LOGIN_USER}</CardTitle>
            <p className={`text-sm text-center ${
              isDarkMode ? "text-gray-300" : "text-gray-500"
            }`}>
              {needPasswordChange 
                ? textConfig.LOGIN_CHANGE_PASSWORD_DESCRIPTION
                : textConfig.LOGIN_NORMAL_DESCRIPTION}
            </p>
            <div className="absolute top-4 right-4">
              {(isLoading || isADLoading) && (
                <Loader2 className={`h-6 w-6 animate-spin ${isDarkMode ? "text-white" : "text-gray-700"}`} />
              )}
              {!isLoading && !isADLoading && loginStatus === 'success' && (
                <CheckCheck className="h-6 w-6 text-green-500" />
              )}
              {!isLoading && !isADLoading && loginStatus === 'error' && (
                <X className="h-6 w-6 text-red-500" />
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {!needPasswordChange ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-4">
                  <Input
                    type="text"
                    placeholder={textConfig.LOGIN_PLACEHOLDER_USERNAME}
                    value={user}
                    onChange={(e) => setUser(e.target.value)}
                    disabled={isLoading || isADLoading}
                  />
                  <div className={`text-xs text-center ${
                    isDarkMode ? "text-gray-500" : "text-gray-700"
                  }`}>
                    {textConfig.LOGIN_USER_DESCRIPTION}
                  </div>
                </div>
                <div className="space-y-1 relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder={textConfig.LOGIN_PLACEHOLDER_PASSWORD}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading || isADLoading}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('password')}
                    className="absolute inset-y-0 right-3 flex items-center"
                    aria-label={showPassword ? textConfig.LOGIN_HIDE_PASSWORD : textConfig.LOGIN_SHOW_PASSWORD}
                    disabled={isLoading || isADLoading}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-700" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-700" />
                    )}
                  </button>
                </div>
                {errorMessage && (
                  <div className="text-xs text-center text-red-500">
                    {errorMessage}
                  </div>
                )}
                
                <div className="space-y-3 pt-2">
                  <div className="flex gap-4">
                    <Button 
                      variant="primary" 
                      className="w-full" 
                      type="submit"
                      disabled={isLoading || isADLoading}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {textConfig.LOGIN_BUTTON_LOADING}
                        </div>
                      ) : (
                        textConfig.LOGIN_BUTTON_LOGIN
                      )}
                    </Button>
                    {!isConfigLoading && config?.GUEST_ALLOWED && (
                      <Button
                        variant="secondary"
                        className="w-full"
                        type="button"
                        onClick={handleGuestLogin}
                        disabled={isLoading || isADLoading}
                      >
                        {textConfig.LOGIN_BUTTON_GUEST}
                      </Button>
                    )}
                  </div>
                  
                  {showADButton && (
                    <>
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-gray-300" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className={`px-2 ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'}`}>
                            O
                          </span>
                        </div>
                      </div>                 
                      <ADLoginButton
                        onClick={handleADLogin}
                        isLoading={isADLoading}
                        disabled={isLoading}
                        textConfig={textConfig}
                      />
                    </>
                  )}
                </div>
              </form>
            ) : (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-1 relative">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    placeholder={textConfig.LOGIN_PLACEHOLDER_NEW_PASSWORD}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('newPassword')}
                    className="absolute inset-y-0 right-3 flex items-center"
                    aria-label={showNewPassword ? textConfig.LOGIN_HIDE_PASSWORD : textConfig.LOGIN_SHOW_PASSWORD}
                    disabled={isLoading}
                  >
                    {showNewPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-700" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-700" />
                    )}
                  </button>
                </div>
                
                {newPassword.length > 0 && (
                  <div className={`text-xs px-2 py-1 rounded-md ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-100"
                  }`}>
                    <p className={`font-semibold mb-1 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}>
                      {textConfig.LOGIN_PASSWORD_REQUIREMENTS_TITLE}
                    </p>
                    <div className={`flex items-center gap-1 ${
                      passwordValidations.length ? "text-green-500" : "text-red-500"
                    }`}>
                      {passwordValidations.length ? <CheckCircle2 size={12}/> : <AlertTriangle size={12}/>}
                      <p>• {textConfig.LOGIN_PASSWORD_CRITERIA?.LENGTH}</p>
                    </div>
                    <div className={`flex items-center gap-1 ${
                      passwordValidations.uppercase ? "text-green-500" : "text-red-500"
                    }`}>
                      {passwordValidations.uppercase ? <CheckCircle2 size={12}/> : <AlertTriangle size={12}/>}
                      <p>• {textConfig.LOGIN_PASSWORD_CRITERIA?.UPPERCASE}</p>
                    </div>
                    <div className={`flex items-center gap-1 ${
                      passwordValidations.lowercase ? "text-green-500" : "text-red-500"
                    }`}>
                      {passwordValidations.lowercase ? <CheckCircle2 size={12}/> : <AlertTriangle size={12}/>}
                      <p>• {textConfig.LOGIN_PASSWORD_CRITERIA?.LOWERCASE}</p>
                    </div>
                    <div className={`flex items-center gap-1 ${
                      passwordValidations.number ? "text-green-500" : "text-red-500"
                    }`}>
                      {passwordValidations.number ? <CheckCircle2 size={12}/> : <AlertTriangle size={12}/>}
                      <p>• {textConfig.LOGIN_PASSWORD_CRITERIA?.NUMBER}</p>
                    </div>
                    <div className={`flex items-center gap-1 ${
                      passwordValidations.special ? "text-green-500" : "text-red-500"
                    }`}>
                      {passwordValidations.special ? <CheckCircle2 size={12}/> : <AlertTriangle size={12}/>}
                      <p>• {textConfig.LOGIN_PASSWORD_CRITERIA?.SPECIAL_CHAR}</p>
                    </div>
                  </div>
                )}
                <div className="space-y-1 relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={textConfig.LOGIN_PLACEHOLDER_CONFIRM_PASSWORD}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirmPassword')}
                    className="absolute inset-y-0 right-3 flex items-center"
                    aria-label={showConfirmPassword ? textConfig.LOGIN_HIDE_PASSWORD : textConfig.LOGIN_SHOW_PASSWORD}
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-700" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-700" />
                    )}
                  </button>
                </div>
                
                {confirmNewPassword.length > 0 && (
                  <div className={`flex items-center gap-1 text-xs ${
                    newPassword === confirmNewPassword ? "text-green-500" : "text-red-500"
                  }`}>
                    {newPassword === confirmNewPassword ? 
                      <CheckCircle2 size={12}/> : 
                      <AlertTriangle size={12}/>
                    }
                    <p>
                      {newPassword === confirmNewPassword ? 
                        textConfig.LOGIN_PASSWORDS_MATCH : 
                        textConfig.LOGIN_PASSWORDS_MISMATCH
                      }
                    </p>
                  </div>
                )}
                
                {errorMessage && (
                  <div className="text-xs text-center text-red-500">
                    {errorMessage}
                  </div>
                )}
                <Button 
                  variant="primary" 
                  className="w-full" 
                  type="submit"
                  disabled={!isPasswordValid() || newPassword !== confirmNewPassword || isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {textConfig.LOGIN_BUTTON_LOADING}
                    </div>
                  ) : (
                    textConfig.LOGIN_BUTTON_CHANGE_PASSWORD
                  )}
                </Button>
              </form>
            )}
            <div className="flex items-stretch rounded-lg shadow-md overflow-hidden">
              <div className="flex items-center justify-center bg-blue-500 w-14 p-4">
                <Info className="text-white w-6 h-6" />
              </div>
              <div className="flex items-center bg-blue-100 p-2">
                <p className="text-blue-500 text-xs">
                  {needPasswordChange
                    ? textConfig.LOGIN_SECURITY_INFO
                    : textConfig.LOGIN_GUEST_INFO}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <CustomAlert
        open={alertConfig.open}
        onClose={() => setAlertConfig((prev) => ({ ...prev, open: false }))}
        onConfirm={alertConfig.onConfirm}
        type={alertConfig.type}
        message={alertConfig.message}
        title={alertConfig.title}
        $isDark={isDarkMode}
        textConfig={textConfig}
      />
    </div>
  );
}

export default Login;